import { createStore } from '@mpxjs/core'

/**
 * @typedef {Object} Product
 * @property {string} id
 * @property {string} name
 * @property {number} price
 * @property {string[]} images
 * @property {string} category
 * @property {string} [description]
 * @property {string} [spec]
 * @property {string} [shelfLife]
 * @property {string} [cartonSpec]
 * @property {string} [minOrder]
 * @property {string[]} [tags]
 * @property {number} [retailPrice]
 * @property {string} [features]
 */

/**
 * @typedef {Object} Category
 * @property {string} id
 * @property {string} name
 */

/**
 * @typedef {Object} AgentInfo
 * @property {string} agentId
 * @property {string} name
 * @property {string} companyName
 * @property {string} phone
 * @property {string} contactName
 */

/**
 * @typedef {Object} Company
 * @property {string} id
 * @property {string} agentId
 * @property {string} name
 * @property {string} contactName
 * @property {string} phone
 */

/**
 * @typedef {Object} CartItem
 * @property {string} productId
 * @property {number} quantity
 * @property {string} [specId]
 */

/**
 * @typedef {Object} State
 * @property {string} apiBase
 * @property {Product[]} products
 * @property {Category[]} categories
 * @property {AgentInfo|null} agentInfo
 * @property {Company[]} companies
 * @property {CartItem[]} cart
 * @property {string|null} lastError
 * @property {'INIT'|'FETCHING'|'SUCCESS'|'FAIL'} fetchStatus
 */

const isWeb = process.env.MPX_CURRENT_TARGET_MODE === 'web'
const isDev = process.env.NODE_ENV === 'development'
const defaultApiBase = (isWeb && isDev)
    ? '/api'
    : (isWeb ? '/api' : 'https://tuce.aizhuliren.cn/api')
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=400&h=400&fit=crop'

const normalizeApiBase = (base) => {
    if (!base || typeof base !== 'string') return defaultApiBase
    return base.replace(/\/+$/, '')
}

/**
 * @param {string} apiBase
 * @param {Object} options
 * @param {'GET'|'POST'|'PUT'|'DELETE'|'PATCH'|'OPTIONS'|'HEAD'} options.method
 * @param {string} options.path
 * @param {any} [options.data]
 */
const requestJson = async (apiBase, { method, path, data }) => {
    const safePath = String(path || '')
    const baseUrl = normalizeApiBase(apiBase)
    const url = `${baseUrl}${safePath.startsWith('/') ? '' : '/'}${safePath}`

    if (isWeb) {
        const res = await fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: method === 'GET' ? undefined : JSON.stringify(data)
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
    }

    return new Promise((resolve, reject) => {
        wx.request({
            url,
            method,
            data,
            header: { 'content-type': 'application/json' },
            success: (res) => (res.statusCode >= 200 && res.statusCode < 300) ? resolve(res.data) : reject(new Error(`HTTP ${res.statusCode}`)),
            fail: reject
        })
    })
}

/** @type {State} */
const state = {
    apiBase: defaultApiBase,
    products: [],
    categories: [],
    agentInfo: null,
    companies: [],
    selectedCompanyId: null, // 当前选中的默认公司
    sharedCompanyInfo: null, // 分享快照中的公司信息
    cart: [],
    sharedCart: [], // 来自链接的固定清单
    settings: {}, // 全局设置
    lastError: null,
    fetchStatus: 'INIT'
}

// Helper for persistence
const saveCartToStorage = (cart) => {
    try {
        if (isWeb) {
            localStorage.setItem('tuce_cart', JSON.stringify(cart))
        } else {
            wx.setStorageSync('tuce_cart', cart)
        }
    } catch (e) { console.error('SaveCart Error', e) }
}

const store = createStore({
    state,
    mutations: {
        setApiBase(s, val) { s.apiBase = normalizeApiBase(val) },
        setProducts(s, val) {
            s.products = (val || []).map((p) => ({
                ...p,
                images: (p.images && p.images.length > 0)
                    ? p.images.map((img) => img.includes('placeholder') ? DEFAULT_IMAGE : img)
                    : [DEFAULT_IMAGE]
            }))
        },
        setCategories(s, val) { s.categories = val || [] },
        setAgentInfo(s, val) { s.agentInfo = val },
        setCompanies(s, val) { s.companies = val || [] },
        setCompanyId(s, val) {
            s.selectedCompanyId = val
            if (isWeb) localStorage.setItem('tuce_selected_company', val || '')
            else wx.setStorageSync('tuce_selected_company', val || '')
        },
        removeCompany(s, id) {
            s.companies = s.companies.filter(c => c.id !== id)
            if (s.selectedCompanyId === id) {
                s.selectedCompanyId = s.companies[0]?.id || null
            }
        },
        setSharedCompany(s, val) { s.sharedCompanyInfo = val },
        setCart(s, val) {
            s.cart = val || []
            saveCartToStorage(s.cart)
        },
        setSharedCart(s, val) {
            s.sharedCart = val || []
        },
        setSettings(s, val) {
            const settingsObj = {}
            if (Array.isArray(val)) {
                val.forEach(item => {
                    settingsObj[item.id] = item.value
                })
            }
            s.settings = settingsObj
        },
        addToCart(s, { productId, quantity = 1, specId }) {
            const pid = String(productId)
            const idx = s.cart.findIndex(i => String(i.productId) === pid && i.specId === specId)
            if (idx !== -1) {
                const item = s.cart[idx]
                s.cart.splice(idx, 1, { ...item, quantity: item.quantity + quantity })
            } else {
                s.cart = [...s.cart, { productId: pid, quantity, specId }]
            }
            saveCartToStorage(s.cart)
        },
        setFetchStatus(s, val) { s.fetchStatus = val },
        setLastError(s, val) { s.lastError = val }
    },
    actions: {
        async init({ commit, dispatch }, { apiBase }) {
            try {
                if (apiBase) {
                    commit('setApiBase', apiBase)
                    wx.setStorageSync('tuce_api_base', apiBase)
                } else {
                    const savedBase = wx.getStorageSync('tuce_api_base')
                    if (savedBase) commit('setApiBase', savedBase)
                }

                const savedAgent = isWeb ? localStorage.getItem('tuce_agent_info') : wx.getStorageSync('tuce_agent_info')
                if (savedAgent) {
                    try {
                        const info = typeof savedAgent === 'string' ? JSON.parse(savedAgent) : savedAgent
                        commit('setAgentInfo', info)
                        dispatch('fetchAgentCompanies')
                    } catch (e) { }
                }

                let savedCart = []
                if (isWeb) {
                    const raw = localStorage.getItem('tuce_cart')
                    if (raw) savedCart = JSON.parse(raw)
                } else {
                    savedCart = wx.getStorageSync('tuce_cart')
                }

                if (Array.isArray(savedCart)) {
                    const cleanCart = savedCart.map((i) => ({ ...i, productId: String(i.productId) }))
                    commit('setCart', cleanCart)
                }

                const savedCompany = isWeb ? localStorage.getItem('tuce_selected_company') : wx.getStorageSync('tuce_selected_company')
                if (savedCompany) commit('setCompanyId', savedCompany)
            } catch (e) { console.error('Init Error', e) }
        },
        async fetchBootstrap({ commit, state }) {
            commit('setFetchStatus', 'FETCHING')
            try {
                console.log('[Store] fetchBootstrap starting, apiBase:', state.apiBase)
                const [categories, products, settings] = await Promise.all([
                    requestJson(state.apiBase, { method: 'GET', path: '/categories' }),
                    requestJson(state.apiBase, { method: 'GET', path: '/products' }),
                    requestJson(state.apiBase, { method: 'GET', path: '/settings' }).catch(() => [])
                ])
                console.log('[Store] fetchBootstrap success', {
                    catCount: categories?.length,
                    prodCount: products?.length,
                    settingsCount: settings?.length
                })
                commit('setCategories', categories)
                commit('setProducts', products)
                commit('setSettings', settings)
                commit('setFetchStatus', 'SUCCESS')
            } catch (e) {
                console.error('[Store] fetchBootstrap error:', e)
                commit('setFetchStatus', 'FAIL')
                commit('setLastError', e.message)
            }
        },
        async fetchAgentCompanies({ commit, state }) {
            const agentId = state.agentInfo?.id
            if (!agentId) return
            try {
                const companies = await requestJson(state.apiBase, {
                    method: 'GET',
                    path: `/companies?agentId=${agentId}`
                })
                commit('setCompanies', Array.isArray(companies) ? companies.filter((c) => c.agentId === agentId) : [])
            } catch (e) {
                console.error('[Store] fetchAgentCompanies error:', e)
            }
        },
        async loginByUid({ commit, state, dispatch }, uid) {
            try {
                const agents = await requestJson(state.apiBase, {
                    method: 'GET',
                    path: `/agents?q=${uid}`
                })
                const agent = agents.find(a => a.uid === uid)
                if (agent) {
                    commit('setAgentInfo', agent)
                    if (isWeb) localStorage.setItem('tuce_agent_info', JSON.stringify(agent))
                    else wx.setStorageSync('tuce_agent_info', agent)
                    await dispatch('fetchAgentCompanies')
                    return agent
                }
                throw new Error('未找到该合作商识别码')
            } catch (e) {
                console.error('[Store] loginByUid error:', e)
                throw e
            }
        },
        async logout({ commit }) {
            commit('setAgentInfo', null)
            commit('setCompanies', [])
            if (isWeb) localStorage.removeItem('tuce_agent_info')
            else wx.removeStorageSync('tuce_agent_info')
        },
        async loginByWechat({ commit, state, dispatch }, code) {
            try {
                // 调用后端 API,用 code 换取 agent 信息
                const result = await requestJson(state.apiBase, {
                    method: 'POST',
                    path: '/agents/login-by-wechat',
                    data: { code }
                })

                if (result.agent) {
                    commit('setAgentInfo', result.agent)
                    if (isWeb) localStorage.setItem('tuce_agent_info', JSON.stringify(result.agent))
                    else wx.setStorageSync('tuce_agent_info', result.agent)

                    // 设置公司列表
                    if (result.companies) {
                        commit('setCompanies', result.companies)
                    } else {
                        await dispatch('fetchAgentCompanies')
                    }

                    return result.agent
                }
                throw new Error('微信授权登录失败')
            } catch (e) {
                console.error('[Store] loginByWechat error:', e)
                throw e
            }
        },
        async saveCompany({ state, dispatch }, company) {
            const method = company.id ? 'PATCH' : 'POST'
            const path = company.id ? `/companies/${company.id}` : '/companies'
            const agentId = state.agentInfo?.id
            if (!agentId) return
            await requestJson(state.apiBase, {
                method,
                path,
                data: { ...company, agentId }
            })
            await dispatch('fetchAgentCompanies')
        },
        async deleteCompany({ state, commit }, id) {
            await requestJson(state.apiBase, {
                method: 'DELETE',
                path: `/companies/${id}`
            })
            commit('removeCompany', id)
        },
        async createCartShare({ state }, { items }) {
            // 单公司模式:直接使用第一个公司
            const companyId = state.companies.length > 0 ? state.companies[0].id : null
            const res = await requestJson(state.apiBase, {
                method: 'POST',
                path: '/cart-shares',
                data: {
                    items,
                    agentId: state.agentInfo?.id,
                    companyId // 使用第一个公司的 ID
                }
            })
            return res
        },
        async fetchShareConfig({ state, commit }, shareId) {
            try {
                const res = await requestJson(state.apiBase, {
                    method: 'GET',
                    path: `/cart-shares/${shareId}`
                })
                if (res && res.items) {
                    // 转换 items 格式（后端返回的是展开的格式）
                    const cartItems = res.items.map(item => ({
                        productId: item.productId,
                        quantity: item.quantity,
                        specId: item.specId
                    }))
                    commit('setSharedCart', cartItems)
                    // 后端现在直接返回 company 对象
                    if (res.company) {
                        commit('setSharedCompany', res.company)
                    }
                }
            } catch (e) {
                console.error('[Store] fetchShareConfig error:', e)
            }
        },
        async wechatLogin({ state }, code) {
            return await requestJson(state.apiBase, {
                method: 'GET',
                path: `/wechat/login?code=${code}`
            })
        }
    }
})

export default store
